import { useMemo } from 'react';
import { Button } from '@crazyegginc/hatch';

const CUSTOMER_ID_GROUPS = /^(\d{3})(\d{3})(\d{4})$/;

export function GoogleAdsAccountSelectionDialog({
  customers = [],
  errorMessage = null,
  pendingCustomerId = null,
  onDismiss,
  onSelect,
}) {
  const uniqueCustomers = useMemo(() => {
    const dedupedCustomers = new Map();

    customers.forEach((customer) => {
      if (customer?.id && !dedupedCustomers.has(customer.id)) {
        dedupedCustomers.set(customer.id, customer);
      }
    });

    return Array.from(dedupedCustomers.values());
  }, [customers]);

  if (uniqueCustomers.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[99998]">
      <button
        type="button"
        className="absolute inset-0 bg-mako-500/60"
        aria-label="Dismiss Google Ads account picker"
        onClick={onDismiss}
      />
      <div
        role="dialog"
        aria-labelledby="google-ads-account-selection-title"
        aria-modal="true"
        className="absolute left-1/2 top-1/2 z-[99999] flex max-h-[80vh] w-[560px] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 flex-col overflow-auto rounded-[28px] bg-white p-7 shadow-2xl"
      >
        <div className="inline-flex w-fit items-center rounded-full border border-[#D7E7FF] bg-[#F5FAFF] px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-[#00509D]">
          Google Ads
        </div>

        <h2
          id="google-ads-account-selection-title"
          className="mt-4 text-[28px] font-semibold leading-tight text-black-pearl-500"
        >
          Choose the Google Ads account to connect
        </h2>

        <p className="mt-3 text-sm leading-6 text-lynch-500">
          This Google login can access more than one Ads customer. Pick the account you want to attach to the currently
          selected site.
        </p>

        {errorMessage ? (
          <div
            className="mt-4 rounded-2xl border border-[#F8D7DA] bg-[#FFF5F5] px-4 py-3 text-sm font-medium text-[#8A2432]"
            data-testid="google-ads-selection-error"
          >
            {errorMessage}
          </div>
        ) : null}

        <div className="mt-6 flex flex-col gap-3">
          {uniqueCustomers.map((customer) => {
            const { id: customerId, name } = customer;
            const formattedCustomerId = formatGoogleAdsCustomerId(customerId);
            const loading = pendingCustomerId === customerId;

            return (
              <button
                key={customerId}
                type="button"
                className="flex items-center justify-between rounded-2xl border border-mystic-500 px-4 py-3 text-left transition hover:border-[#BFD7FF] hover:bg-[#F5FAFF]"
                onClick={() => onSelect(customerId)}
                disabled={Boolean(pendingCustomerId)}
                data-testid={`google-ads-account-${customerId}`}
              >
                <div>
                  {name ? <div className="text-sm font-semibold text-black-pearl-500">{name}</div> : null}
                  <div className={`text-xs font-medium text-lynch-500 ${name ? 'mt-1' : ''}`}>
                    {formattedCustomerId}
                  </div>
                </div>
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#00509D]">
                  {loading ? 'Connecting...' : 'Connect'}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-end">
          <Button variant="cancel" onClick={onDismiss}>
            Not now
          </Button>
        </div>
      </div>
    </div>
  );
}

function formatGoogleAdsCustomerId(customerId) {
  if (typeof customerId !== 'string') {
    return customerId;
  }

  const normalized = customerId.replace(/\D/g, '');

  return normalized.replace(CUSTOMER_ID_GROUPS, '$1-$2-$3');
}
